'use client'

import { type CSSProperties, useCallback, useEffect, useId, useRef, useState } from 'react'

import { cn } from '@/lib/utils'

/**
 * React Bits' `Code Slots`, vendored — one square per character, the character
 * dropping into its square, and the whole row spilling out from the end when
 * what was typed is refused.
 *
 * Taken from `DavidHDev/react-bits` rather than installed, for the reason
 * `WarpText` and `MetallicPaint` give in this folder: the library ships source
 * to copy, not a package to depend on. The original also wants `motion`, which
 * this app does not have and is not getting — a square that bounces once is one
 * call to `element.animate`.
 *
 * ── WHAT WAS CHANGED, AND WHY ────────────────────────────────────────────
 * The original is an OTP field and every one of its assumptions is an OTP
 * assumption. This one holds DISCOUNT CODES, and those are a different shape of
 * thing: `FLOUTEST` sits in the database, the order form suggests `WIOSNA24`,
 * and `createCodeSchema` accepts anything matching `/^[A-Za-z0-9-]+$/` from 3
 * to 64 characters. Three deliberate departures, marked `FLOU:` below —
 *
 *  1. The alphabet. The original filters input through `replace(/\D/g, '')` and
 *     gates each keystroke on `/^[0-9]$/`. Here it is `[A-Za-z0-9-]`, upper-cased
 *     on the way in — the same sanitising `DiscountCodes` and the order form
 *     already do at their own inputs, because the column is case-insensitively
 *     unique and nobody types `WIOSNA24` the way it was written down.
 *  2. The slot count is not fixed. `maxLength` is a ceiling, not a length: the
 *     row shows as many slots as there are characters PLUS one waiting for the
 *     next, never fewer than `minSlots`. A code of unknown length in a row of
 *     twelve empty boxes reads as a form that has not been filled in — the row
 *     has to grow with the typing rather than stand there counting down.
 *  3. There is no `onComplete`. A variable-length code has no moment at which it
 *     becomes complete, so there is nothing to fire on; the person says when
 *     they are done, which is `onSubmit` on Enter. Every keystroke reports
 *     through `onChange` and the caller owns the value.
 *
 * ── WHY ONE REAL INPUT UNDERNEATH ────────────────────────────────────────
 * The squares are a PICTURE. Beneath them lies a single ordinary `<input>`,
 * transparent and stretched across the whole row, and that is where the text
 * actually lives. Everything a text field gets for free then keeps working:
 * pasting a code out of an e-mail, the browser's own clipboard suggestion,
 * select-all, arrow keys, undo, and — the one that decides it — the phone's
 * on-screen keyboard, which opens for a focused input and for nothing else.
 * The alternative, one `<input maxlength="1">` per square with focus shoved
 * between them, is how the original does it and it breaks all of the above:
 * a pasted code lands in box one, and every mobile keyboard fights the focus
 * jumps.
 *
 * ── WHY A REFUSED CODE IS NOT CLEARED ────────────────────────────────────
 * `status='error'` spills the characters and outlines the row in
 * `--panel-down`, and then puts the characters back. It does not empty the
 * field. A refused discount code is usually a typo in a code that IS valid, and
 * wiping the row makes the person type all eight characters again to fix one of
 * them. If a caller really wants it emptied it owns `value` and can do that.
 */
/**
 * Asked at animation time rather than during render, and outside the component
 * rather than inside it. Outside, because a function rebuilt on every render is
 * a dependency that changes on every render, and every effect below would have
 * had to list it. At animation time, because the server has no media query to
 * answer with — a value baked into the markup would be the wrong one for
 * whichever half of the readers the server guessed against.
 */
const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false)

export function CodeSlots({
  value,
  onChange,
  onSubmit,
  status = 'idle',
  maxLength = 12,
  minSlots = 6,
  disabled = false,
  ariaLabel = 'Kod rabatowy',
  className,
}: {
  /** The code as it stands. Sanitised on the way out of `onChange`, never here. */
  value: string
  /** Every keystroke, already stripped to `[A-Z0-9-]` and cut to `maxLength`. */
  onChange: (next: string) => void
  /** Enter. There is no "complete" for a variable-length code — this is it. */
  onSubmit?: () => void
  /** `error` spills the row red, `success` locks it green. */
  status?: 'idle' | 'error' | 'success'
  /** The ceiling, not the length. Slots stop appearing here. */
  maxLength?: number
  /** How few slots the row may shrink to, so an empty field still looks like one. */
  minSlots?: number
  disabled?: boolean
  /** What a screen reader calls the field. The squares themselves are decoration. */
  ariaLabel?: string
  className?: string
}) {
  const [focused, setFocused] = useState(false)
  const statusId = useId()

  const slotRefs = useRef<Array<HTMLSpanElement | null>>([])
  const charRefs = useRef<Array<HTMLSpanElement | null>>([])
  const caretRef = useRef<HTMLSpanElement>(null)

  // FLOU: one slot per character plus one waiting for the next, floored at
  // `minSlots` and capped at `maxLength` — see departure 2 at the top.
  const targetCount = Math.min(maxLength, Math.max(minSlots, value.length + 1))

  // What is actually on screen. It follows `targetCount` up at once and down
  // only after the leaving squares have finished leaving, because a slot that
  // is already unmounted cannot animate its way out.
  const [renderedCount, setRenderedCount] = useState(targetCount)

  const previousLength = useRef(value.length)
  const previousRendered = useRef(renderedCount)
  // What `status` was when an effect last looked. The spill fires on the CHANGE
  // into `error` rather than on the state — see the note on that effect.
  const previousStatus = useRef(status)

  const locked = status === 'success'

  // The square the next character lands in, or `-1` when there is no next
  // character: the row is full, the code was accepted, or the field is off.
  // One value rather than three separate conditions, because the caret, the
  // edge colour and the halo all have to agree about which square that is. At
  // `maxLength` the plain `Math.min` clamped back onto the last FILLED square,
  // which then sat there lit up as "type here" over a row with nowhere left to
  // type — the caret knew better and hid, the border and halo did not.
  const activeIndex =
    locked || disabled || value.length >= maxLength ? -1 : Math.min(value.length, renderedCount - 1)
  const caretVisible = focused && activeIndex >= 0

  const sanitize = useCallback(
    (raw: string) =>
      raw
        .replace(/[^A-Za-z0-9-]/g, '')
        .toUpperCase()
        .slice(0, maxLength),
    [maxLength],
  )

  // Grow now, shrink later. The shrink waits on the exit animation and is
  // cancelled if the person keeps typing, which happens constantly — backspace
  // and then a new character is two of these in the same second.
  useEffect(() => {
    if (targetCount === renderedCount) return
    if (targetCount > renderedCount || prefersReducedMotion()) {
      setRenderedCount(targetCount)
      return
    }

    const leaving = slotRefs.current.slice(targetCount, renderedCount).filter(Boolean)
    if (leaving.length === 0) {
      setRenderedCount(targetCount)
      return
    }

    let cancelled = false
    const animations = leaving.map((slot, index) =>
      slot?.animate(
        [
          { opacity: 1, transform: 'scale(1)' },
          { opacity: 0, transform: 'scale(0.5)' },
        ],
        {
          duration: 170,
          // The furthest square goes first, so the row unzips from its end
          // rather than collapsing all at once.
          delay: (leaving.length - 1 - index) * 40,
          easing: 'cubic-bezier(0.4, 0, 1, 1)',
          fill: 'forwards',
        },
      ),
    )

    // The one with the longest delay finishes last, and that is the lowest
    // index — the squares leave in reverse.
    animations[0]?.finished
      .then(() => {
        if (!cancelled) setRenderedCount(targetCount)
      })
      .catch(() => {
        // `finished` rejects when the animation is cancelled, which is the
        // normal path out of here and not a failure.
      })

    return () => {
      cancelled = true
      for (const animation of animations) animation?.cancel()
    }
  }, [renderedCount, targetCount])

  // A new square arriving. Separate from the one above because a square can
  // also arrive from a paste, several at a time, and they should not all pop
  // in on the same frame.
  useEffect(() => {
    const from = previousRendered.current
    previousRendered.current = renderedCount
    if (renderedCount <= from || prefersReducedMotion()) return

    const entering = slotRefs.current.slice(from, renderedCount).filter(Boolean)
    const animations = entering.map((slot, index) =>
      slot?.animate(
        [
          { opacity: 0, transform: 'scale(0.55)' },
          { opacity: 1, transform: 'scale(1)' },
        ],
        {
          duration: 200,
          delay: index * 40,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          // `backwards` so a square waiting out its delay is invisible instead
          // of sitting there at full size until its turn comes.
          fill: 'backwards',
        },
      ),
    )
    return () => {
      for (const animation of animations) animation?.cancel()
    }
  }, [renderedCount])

  // The character landing in its square. Only ever the last one, and only when
  // the code got longer — backspace and paste are not a drop.
  useEffect(() => {
    const grew = value.length > previousLength.current
    const index = value.length - 1
    previousLength.current = value.length
    if (!grew || index < 0 || prefersReducedMotion()) return

    const animation = charRefs.current[index]?.animate(
      [
        { opacity: 0, transform: 'translateY(-60%) scale(0.7)' },
        { opacity: 1, transform: 'translateY(10%) scale(1.08)', offset: 0.62 },
        { opacity: 1, transform: 'translateY(0) scale(1)' },
      ],
      { duration: 240, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
    )
    return () => animation?.cancel()
  }, [value])

  // The refusal. The characters tip out of their squares from the last one
  // backwards and drop back in — the cascade is what says "this code", where a
  // flat red border only says "something on this screen".
  //
  // Fired on the CHANGE into `error`, not on being in it. The cascade has to
  // read `value.length` to know how many characters to tip, and listing that as
  // a dependency made every keystroke a fresh spill: the row threw its
  // characters out again on each key pressed to fix the typo, which is the one
  // thing the note at the top of this file says not to get in the way of.
  //
  // The caller therefore owns the reset. To spill a second time for a second
  // refusal, put `status` back to `idle` in between — `onChange` is the natural
  // place — otherwise refusing an unchanged code twice is silent.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `value.length` sizes the cascade but must not re-trigger it, and `status` is the only trigger — see above.
  useEffect(() => {
    const entered = status === 'error' && previousStatus.current !== 'error'
    previousStatus.current = status
    if (!entered || prefersReducedMotion()) return

    const filled = charRefs.current.slice(0, value.length).filter(Boolean)
    const animations = filled.map((char, index) =>
      char?.animate(
        [
          { opacity: 1, transform: 'translateY(0) rotate(0deg)' },
          { opacity: 0.2, transform: 'translateY(42%) rotate(-8deg)', offset: 0.45 },
          { opacity: 1, transform: 'translateY(0) rotate(0deg)' },
        ],
        {
          duration: 420,
          delay: (filled.length - 1 - index) * 55,
          easing: 'cubic-bezier(0.36, 0, 0.66, 1)',
        },
      ),
    )
    return () => {
      for (const animation of animations) animation?.cancel()
    }
  }, [status])

  // The caret. Steady rather than blinking under reduced motion: a bar that
  // flashes twice a second is motion, whatever convention says about carets.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `activeIndex` is the trigger, not a read — the bar moves square by square and each move needs a fresh animation on the new node.
  useEffect(() => {
    const caret = caretRef.current
    if (!caretVisible || !caret || prefersReducedMotion()) return
    const animation = caret.animate(
      [{ opacity: 1 }, { opacity: 1, offset: 0.5 }, { opacity: 0, offset: 0.5 }, { opacity: 0 }],
      { duration: 1100, iterations: Number.POSITIVE_INFINITY },
    )
    return () => animation.cancel()
  }, [caretVisible, activeIndex])

  // `--panel-accent-solid` and not `--panel-accent`: the latter is not a token
  // this app has. It was written with `oklch(1 0 0)` as a fallback, which meant
  // the fallback was the only branch that ever ran — the square taking the next
  // character was outlined in plain white in both themes, and the panel's one
  // "you are here" colour never appeared. `globals.css` declares the solid form
  // twice, darker on paper, so this follows the theme rather than fighting it.
  const edgeColor = (index: number): string => {
    if (status === 'error') return 'var(--panel-down)'
    if (status === 'success') return 'var(--panel-up)'
    if (focused && index === activeIndex)
      return 'color-mix(in oklab, var(--panel-accent-solid) 70%, transparent)'
    return 'var(--panel-track)'
  }

  // A halo rather than a thicker border: a border that grows moves everything
  // inside the square by a pixel, and a whole row of characters twitching is
  // what the eye reads first — before the colour, which is the actual message.
  const halo = (index: number): string => {
    if (status === 'error')
      return '0 0 0 3px color-mix(in oklab, var(--panel-down) 22%, transparent)'
    if (status === 'success')
      return '0 0 0 3px color-mix(in oklab, var(--panel-up) 20%, transparent)'
    // Marks the square the next character goes into, and only while the field
    // has focus — an unfocused row should read as one object, not as a row with
    // one square lit up for a reason nobody can act on.
    if (focused && index === activeIndex)
      return '0 0 0 3px color-mix(in oklab, var(--panel-accent-solid) 14%, transparent)'
    return 'none'
  }

  const slotStyle = (index: number): CSSProperties => ({
    background: 'var(--panel-card-raised)',
    borderColor: edgeColor(index),
    boxShadow: halo(index),
  })

  const slots = Array.from({ length: renderedCount }, (_, index) => value[index] ?? '')

  return (
    // Dimmed when off, the same 60% `FuseButton` uses. A row of squares that
    // takes no typing has to look different from one that does — the input
    // underneath carries the state for anything that reads the markup, but a
    // sighted person only has the squares, and `disabled` changed nothing about
    // them.
    <div className={cn('relative w-fit max-w-full', disabled && 'opacity-60', className)}>
      {/* Decoration, every square of it. The text these draw is already in the
          input below, and a screen reader that read both would spell the code
          out twice. */}
      <div
        aria-hidden="true"
        className="pointer-events-none flex flex-wrap items-center gap-1.5 sm:gap-2"
      >
        {slots.map((char, index) => (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: position IS the identity of a slot — the third square is the third character and nothing reorders.
            key={index}
            ref={(node) => {
              slotRefs.current[index] = node
            }}
            className={cn(
              'relative flex size-11 shrink-0 items-center justify-center rounded-xl border',
              'text-[1.05rem] font-semibold tracking-[0.06em] tabular-nums',
              char ? 'text-white' : 'text-white/40',
            )}
            style={slotStyle(index)}
          >
            <span
              ref={(node) => {
                charRefs.current[index] = node
              }}
            >
              {char}
            </span>
            {caretVisible && index === activeIndex ? (
              <span
                ref={caretRef}
                className="absolute h-5 w-0.5 rounded-full"
                style={{ background: 'var(--panel-accent-solid)' }}
              />
            ) : null}
          </span>
        ))}
      </div>

      {/* The field itself — see the note at the top of the file. Transparent
          text and caret, because the squares above are already drawing both;
          the ring is on this element and not on the squares so that the focus
          outline surrounds the whole row, which is what one field looks like. */}
      <input
        type="text"
        value={value}
        // `readOnly` and not `disabled` for an accepted code. Disabling would
        // drop the field out of the tab order and out of most screen readers'
        // reach at exactly the moment its contents became worth reading, and it
        // would stop the person copying the code they just had accepted.
        // `readOnly` locks the typing, which is all that "locked" has to mean.
        readOnly={locked}
        disabled={disabled}
        // No `maxLength` attribute. The browser applies it to the RAW string,
        // before anything is stripped, and it truncates a paste rather than
        // rejecting it — so pasting `Kod: WIOSNA24` out of an e-mail got cut to
        // twelve raw characters first and sanitised down to `KODWIOSNA2`, a
        // wrong code arrived at silently. `sanitize` strips and only then cuts,
        // which is the order that survives a paste with anything around it, and
        // it is the only cap: React puts the field back to `value` after every
        // change event, so a thirteenth keystroke never survives the event.
        aria-label={ariaLabel}
        aria-invalid={status === 'error'}
        // Points at the live region below, so the refusal is also there for
        // somebody who tabs BACK into the field afterwards. A live region only
        // speaks at the moment it changes; `aria-describedby` is what makes it
        // readable again on arrival.
        aria-describedby={statusId}
        autoComplete="off"
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        inputMode="text"
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(event) => onChange(sanitize(event.target.value))}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return
          event.preventDefault()
          onSubmit?.()
        }}
        className={cn(
          'absolute inset-0 h-full w-full rounded-2xl bg-transparent px-0 text-transparent caret-transparent',
          'focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:outline-none',
          'disabled:cursor-default',
          locked ? 'cursor-default' : 'cursor-text',
        )}
      />

      {/* The row turning red or green is invisible to a reader who cannot see
          it, and the refusal is the whole point of the component. */}
      <span id={statusId} aria-live="polite" className="sr-only">
        {status === 'error' ? 'Kod odrzucony.' : status === 'success' ? 'Kod przyjęty.' : ''}
      </span>
    </div>
  )
}
